<script lang="ts">
  import MusicList from '@/components/common/MusicList/MusicList.svelte'
  import { query, replace } from '@/plugins/routes'
  import { getListMusics } from '@/modules/musicLibrary/actions'
  import { musicLibraryEvent } from '@/modules/musicLibrary/store/event'
  import type { ListInfo } from '@/components/common/MusicList/type'
  import { useListCover, userListInited, userListsAll } from '@/modules/musicLibrary/reactive.svelte'
  import { dateFormat } from '@/shared'
  import {
    getListScrollPosition,
    saveListScrollPosition,
    userListExist,
    sortListMusics,
  } from '@/modules/musicLibrary/store/actions'
  import { type ComponentExports, tick, untrack } from 'svelte'
  import { LIST_IDS } from '@any-listen/common/constants'
  import { resourceList } from '@/modules/extension/reactive.svelte'
  import { LIST_PIC_ICON } from '@/shared/constants'

  let list = $state.raw<AnyListen.Music.MusicInfo[]>([])
  let musicList = $state<ComponentExports<typeof MusicList> | null>(null)

  const getTargetActiveListInfo = (allList: typeof $userListsAll, activeId: string) => {
    return allList.find((l) => l.id == activeId)
  }
  const buildActiveListInfo = (
    resourceList: AnyListen.Extension.ResourceList,
    info?: AnyListen.List.MyListInfo,
    listCover?: string | null
  ) => {
    if (!info) return undefined
    switch (info.type) {
      case 'default':
      case 'general':
        return {
          id: info.id,
          name: info.name,
          createTime: info.type == 'default' ? '' : dateFormat(info.meta.createTime, 'Y-M-D'),
          playCount: info.meta.playCount,
          pic: info.type == 'default' ? '' : info.meta.pic || listCover || '',
          picIcon: LIST_PIC_ICON[info.id as keyof typeof LIST_PIC_ICON],
          type: info.type,
        } satisfies ListInfo
      case 'local':
        return {
          id: info.id,
          name: info.name,
          createTime: dateFormat(info.meta.createTime, 'Y-M-D'),
          playCount: info.meta.playCount,
          pic: info.meta.pic || listCover || '',
          type: info.type,
          listMeta: {
            deviceId: info.meta.deviceId,
          },
          getSortTimeFn() {
            return async (list, type) => {
              return sortListMusics(info.id, list, type)
            }
          },
        } satisfies ListInfo
      case 'online':
      case 'remote':
        return {
          id: info.id,
          name: info.name,
          createTime: dateFormat(info.meta.createTime, 'Y-M-D'),
          playCount: info.meta.playCount,
          pic: info.meta.pic || listCover || '',
          type: info.type,
          listMeta: {
            extensionId: info.meta.extensionId,
            source: info.meta.source,
          },
          getSortTimeFn() {
            if (
              info.type === 'remote' &&
              resourceList.listProvider.find((l) => l.extensionId === info.meta.extensionId && l.id === info.meta.source)
                ?.fileSortable
            ) {
              return async (list, type) => {
                return sortListMusics(info.id, list, type)
              }
            }
            return null
          },
        } satisfies ListInfo
    }
  }
  const targetListInfo = $derived(getTargetActiveListInfo($userListsAll, $query.id))
  const listCover = $derived(useListCover(targetListInfo))
  const listInfo = $derived(buildActiveListInfo($resourceList, targetListInfo, listCover.val))

  let currentId = ''
  const handleScroll = (pos: number) => {
    if (!currentId) return
    void saveListScrollPosition(currentId, pos)
  }

  $effect(() => {
    const id = $query.id
    if (id != currentId) {
      currentId = id
      untrack(() => {
        const musicId = $query.mid
        if (!id) return
        void Promise.all([getListMusics(id), getListScrollPosition(id)]).then(([_list, pos]) => {
          list = _list
          void tick().then(() => {
            let idx = -1
            if (musicId) {
              idx = _list.findIndex((m) => m.id == musicId)
              void replace(`/library?id=${id}`)
            }
            if (idx < 0) {
              musicList?.setScrollPosition(pos)
            } else {
              musicList?.setScrollIndex(idx)
            }
          })
        })
      })
    }
    const unsub = musicLibraryEvent.on('listMusicChanged', (ids) => {
      if (!ids.includes(id) || !userListExist(id)) return
      void getListMusics(id).then((_list) => {
        list = [..._list]
      })
    })
    const unsub2 = musicLibraryEvent.on('listMusicUpdated', (updateInfo) => {
      if (!updateInfo.has(id) || !userListExist(id)) return
      void getListMusics(id).then((_list) => {
        list = [..._list]
      })
    })

    return () => {
      unsub()
      unsub2()
    }
  })

  $effect(() => {
    const id = $query.id
    if (!$userListsAll.some((l) => l.id == id) && $userListInited) {
      void replace(`/library?id=${LIST_IDS.LOVE}`)
    }
  })
</script>

<div class="view-container container">
  {#if listInfo}
    <MusicList bind:this={musicList} listinfo={listInfo} {list} onscroll={handleScroll} />
  {/if}
</div>

<style lang="less">
  .container {
    position: relative;
    display: flex;
    flex: auto;
    flex-flow: column nowrap;
    // border-right: 1px solid var(--color-border);
    // background: #fff;
    // overflow: hidden;
    :global(.slider-content) {
      width: 200px;
    }
  }
  // .list {
  //   flex: auto;
  // }
</style>
